import type { LayerKey } from "../api/types";
import { LAYER_STYLES } from "../map/layerStyles";

/** Couleurs de la légende du rapport de terrain, lues dans les styles des couches de la carte (map/layerStyles.ts,
 *  carte gelée, en lecture seule) : la légende montre exactement ce que dessine la carte, sans copie hexadécimale. */

/** Couleur de peinture d'une couche (`fill-color`, `line-color`, `circle-color`) quand c'est une couleur simple. */
export function layerPaintColor(layer: LayerKey, id: string, property: string): string | undefined {
  const value = LAYER_STYLES[layer]?.find((style) => style.id === id)?.paint?.[property];
  return typeof value === "string" ? value : undefined;
}

/** Couleur d'une branche d'une expression `match` (`["match", entrée, v1, c1, v2, c2, défaut]`) ; la valeur par
 *  défaut si la clé n'y figure pas. */
export function matchColor(expression: unknown, key: string): string | undefined {
  if (!Array.isArray(expression) || expression[0] !== "match") return undefined;
  const branches = expression.slice(2);
  for (let i = 0; i + 1 < branches.length; i += 2) {
    const label = branches[i];
    if (label === key || (Array.isArray(label) && label.includes(key))) {
      const color = branches[i + 1];
      return typeof color === "string" ? color : undefined;
    }
  }
  const fallback = branches[branches.length - 1];
  return branches.length % 2 === 1 && typeof fallback === "string" ? fallback : undefined;
}

/** Couleur d'un point de passage obligé selon sa sévérité (HIGH, MEDIUM, autre), celle de la carte. */
export function chokepointColor(severity: string): string | undefined {
  const expression = LAYER_STYLES.chokepoints.find((style) => style.id === "chokepoints-circle")?.paint?.["circle-color"];
  return matchColor(expression, severity);
}

/** Couleurs de l'occupation du sol (forêt, bâti, eau) ; le terrain ouvert, sans couche, prend le fond de l'interface. */
export function landcoverColors(): { forest?: string; urban?: string; water?: string; open: string } {
  return {
    forest: layerPaintColor("forest", "forest-fill", "fill-color"),
    urban: layerPaintColor("urban", "urban-fill", "fill-color"),
    water: layerPaintColor("water", "water-fill", "fill-color"),
    open: "var(--color-canvas)",
  };
}
